import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from '@/components/feedback/states';
import { AppError } from '@obraplan/core/application/errors/app-error';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | undefined;
}

/**
 * Último anteparo contra erro inesperado.
 *
 * A pilha vai para o console, para diagnóstico. A tela recebe mensagem em português e
 * um código — nunca stack trace (PLANO_GERAL.md §4.3).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[obraplan] erro não tratado', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === undefined) return this.props.children;

    const code = AppError.isAppError(error) ? error.code : 'SYSTEM.UNEXPECTED';
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <ErrorState
          title="Algo deu errado"
          message={messageForCode(code)}
          code={code}
          onRetry={() => {
            this.setState({ error: undefined });
            globalThis.location.reload();
          }}
        />
      </div>
    );
  }
}
